import React, { useEffect, useState } from 'react';
import { Accordion, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';

const JBranchCAPModel = ({ show, onHide, capQuestions = [], onSave, checklist, auditorData }) => {
  const [capActions, setCapActions] = useState({});
  const [errors, setErrors] = useState({});
  const [organizedQuestions, setOrganizedQuestions] = useState({});
  const [departmentUsers, setDepartmentUsers] = useState({});
  const [loadingDepartmentUsers, setLoadingDepartmentUsers] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_URL;

  // Fetch existing CAP actions if any
  useEffect(() => {
    const fetchCapActions = async () => {
      if (!checklist?.checklist_id || !checklist?.id || !checklist?.branch_id || !auditorData?.id) {
        console.warn('Missing required data for fetching CAP actions');
        return;
      }

      try {
        const response = await fetch(
          `${BASE_URL}/get-cap-actions/${checklist.checklist_id}/${checklist.id}/${checklist.branch_id}/${auditorData.id}`
        );
        const data = await response.json();
        
        if (data.success) {
          const initialActions = {};
          capQuestions.forEach(question => {
            const existingAction = data.data[question.question_id];
            if (existingAction) {
              initialActions[question.question_id] = {
                responsiblePerson: existingAction.responsiblePerson || '',
                dueDate: existingAction.dueDate || ''
              };
            }
          });
          setCapActions(initialActions);
        }
      } catch (error) {
        console.error('Error fetching CAP actions:', error);
      }
    };

    if (capQuestions.length > 0) {
      fetchCapActions();
    }
  }, [capQuestions, checklist, auditorData, BASE_URL]);

  // Fetch department users based on subsections
  useEffect(() => {
    const fetchDepartmentUsers = async () => {
      if (!capQuestions.length || !checklist?.branch_id) return;
      
      setLoadingDepartmentUsers(true);
      
      try {
        // Extract unique subsections from CAP questions
        const subsections = [...new Set(capQuestions
          .filter(q => q.subsection)
          .map(q => q.subsection))];
          
        if (!subsections.length) {
          console.warn('No subsections found in CAP questions');
          setLoadingDepartmentUsers(false);
          return;
        }
        
        
        // For each subsection, fetch users from matching department
        const usersMap = {};
        
        for (const subsection of subsections) {
          const response = await fetch(
            `${BASE_URL}/get-department-users-by-name?BranchCode=${checklist.BranchCode}&departmentName=${encodeURIComponent(subsection)}`
          );
          
          if (response.ok) {
            const data = await response.json();
            if (data.success && data.users) {
              usersMap[subsection] = data.users;
            } else {
              console.warn(`No users found for subsection: ${subsection}`);
              usersMap[subsection] = [];
            }
          } else {
            console.error(`Failed to fetch users for subsection: ${subsection}`);
            usersMap[subsection] = [];
          }
        }
        
        setDepartmentUsers(usersMap);
      } catch (error) {
        console.error('Error fetching department users:', error);
      } finally {
        setLoadingDepartmentUsers(false);
      }
    };
    
    fetchDepartmentUsers();
  }, [capQuestions, checklist?.BranchCode,checklist?.branch_id, BASE_URL]);

  // Organize questions by section, subsection, and sub-subsection
  useEffect(() => {
    const organizeQuestions = () => {
      const organized = capQuestions.reduce((acc, question) => {
        const section = question.section || 'Other';
        const subsection = question.subsection || 'General';
        const subSubsection = question.subsubsection || null;

        if (!acc[section]) {
          acc[section] = {};
        }
        if (!acc[section][subsection]) {
          acc[section][subsection] = {
            questions: [],
            subSubsections: {}
          };
        }

        if (subSubsection) {
          if (!acc[section][subsection].subSubsections[subSubsection]) {
            acc[section][subsection].subSubsections[subSubsection] = [];
          }
          acc[section][subsection].subSubsections[subSubsection].push(question);
        } else {
          acc[section][subsection].questions.push(question);
        }
        return acc;
      }, {});

      setOrganizedQuestions(organized);
    };

    if (capQuestions.length > 0) {
      organizeQuestions();
    }
  }, [capQuestions]);

  const handleInputChange = (questionId, field, value) => {
    setCapActions(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
    // Clear error when user types
    if (errors[questionId]) {
      setErrors(prev => ({
        ...prev,
        [questionId]: {
          ...prev[questionId],
          [field]: ''
        }
      }));
    }
  };

  // Update validateForm function to require due dates
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Check all capQuestions, not just ones in capActions
    capQuestions.forEach(question => {
      const questionId = question.question_id;
      newErrors[questionId] = {};
      
      // Make sure we have this question in capActions
      if (!capActions[questionId]) {
        // Auto-create an entry for this question if we have department users
        const deptUsers = departmentUsers[question.subsection] || [];
        if (deptUsers.length > 0) {
          const responsiblePerson = `${deptUsers[0].firstName} ${deptUsers[0].lastName}`;
          handleInputChange(questionId, 'responsiblePerson', responsiblePerson);
        }
      }
      
      // Validate due date is required
      if (!capActions[questionId]?.dueDate) {
        newErrors[questionId].dueDate = 'Required';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Updated to handle different action types
  const handleSave = (action = 'saveOnly') => {
    // Skip validation for direct re-audit creation
    if (action === 'createReauditOnly') {
      // Pass empty object for CAP actions when creating re-audit only
      onSave({}, action);
      return;
    }
    
    // For saving CAP actions, validate the form
    if (validateForm()) {
      onSave(capActions, action);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" className="cap-modal">
      <Modal.Header closeButton className="py-3">
        <Modal.Title className="h5">Corrective Action Plan (CAP)</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {loadingDepartmentUsers && (
          <div className="alert alert-info m-3 d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" />
            <span>Loading department users...</span>
          </div>
        )}
        
        <Accordion className="cap-accordion">
          {Object.entries(organizedQuestions).map(([section, subsections], sectionIndex) => (
            <Card key={section} className="mb-2 border-0 rounded-0">
              <Accordion.Item eventKey={section} className="border-0">
                <Accordion.Header className="section-header">
                  <div className="d-flex justify-content-between w-100 align-items-center me-3">
                    <span className="text-truncate">{section}</span>
                    <Badge bg="primary" className="flex-shrink-0 ms-2">
                      {Object.values(subsections).reduce((total, subsection) => 
                        total + subsection.questions.length + 
                        Object.values(subsection.subSubsections).flat().length, 0)
                      } Questions
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body className="p-3 pt-0">
                  {Object.entries(subsections).map(([subsection, data], subsectionIndex) => (
                    <div key={subsection} className="mb-4">
                      <h6 className="border-bottom pb-2 mb-3 d-flex align-items-center flex-wrap">
                        <span className="text-truncate">{subsection}</span>
                      </h6>
                      
                      {/* Regular subsection questions */}
                      {data.questions.map((question) => (
                        <QuestionCard 
                          key={question.question_id}
                          question={question}
                          capActions={capActions}
                          errors={errors}
                          handleInputChange={handleInputChange}
                          departmentUsers={departmentUsers[question.subsection] || []}
                        />
                      ))}

                      {/* Sub-subsections */}
                      {Object.entries(data.subSubsections).map(([subSubsection, questions]) => (
                        <div key={subSubsection} className="ms-md-4 mb-3">
                          <h6 className="border-bottom pb-2 mb-3 text-muted text-truncate">
                            {subSubsection}
                          </h6>
                          {questions.map((question) => (
                            <QuestionCard 
                              key={question.question_id}
                              question={question}
                              capActions={capActions}
                              errors={errors}
                              handleInputChange={handleInputChange}
                              departmentUsers={departmentUsers[question.subsection] || []}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            </Card>
          ))}
        </Accordion>
      </Modal.Body>
      
      <Modal.Footer className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center p-3">
        <Button 
          variant="outline-secondary" 
          onClick={onHide} 
          className="order-3 order-sm-1"
          size="sm"
        >
          <i className="fas fa-times me-1"></i> Close
        </Button>
        
        <div className="d-flex flex-column flex-sm-row gap-2 mt-2 mt-sm-0 order-1 order-sm-2">
          <Button 
            variant="warning"
            onClick={() => handleSave('createReauditOnly')}
            className="text-nowrap"
            size="sm"
          >
            <i className="fas fa-redo-alt me-1"></i> Re-audit Only
          </Button>
          <Button 
            variant="primary"
            onClick={() => handleSave('saveOnly')}
            disabled={!Object.keys(capActions).length}
            size="sm"
          >
            <i className="fas fa-save me-1"></i> Save CAP
          </Button>
        </div>
      </Modal.Footer>

      <style>
        {`
          .cap-modal .modal-xl {
            max-width: min(95%, 1200px);
          }
          
          .cap-modal .modal-content {
            border-radius: 0.5rem;
          }
          
          .cap-accordion .accordion-button:not(.collapsed) {
            background-color: var(--bs-primary);
            color: white;
          }
          
          .cap-accordion .accordion-button:not(.collapsed) .badge {
            background-color: white !important;
            color: var(--bs-primary);
          }
          
          .question-card {
            transition: all 0.2s;
          }
          
          .question-card:hover {
            box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
          }
          
          /* Responsive styles for mobile */
          @media (max-width: 767px) {
            .cap-modal .modal-body {
              padding: 0;
            }
            
            .cap-modal .accordion-body {
              padding: 1rem;
            }
            
            .cap-modal .accordion-button {
              padding: 0.75rem 1rem;
            }
            
            .section-header .badge {
              font-size: 0.65rem;
              padding: 0.25rem 0.5rem;
            }
            
            .cap-modal .modal-footer {
              flex-direction: column-reverse;
            }
          }
          
          /* Better scrolling for modal content */
          .cap-modal .modal-body {
            max-height: calc(100vh - 180px);
            overflow-y: auto;
          }

          .cap-modal .modal-footer {
            border-top: 1px solid #eee;
            padding: 0.75rem 1rem;
          }

          .cap-modal .modal-footer .btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.4rem 0.75rem;
            font-weight: 500;
            border-radius: 4px;
          }

          .cap-modal .modal-footer .btn i {
            font-size: 0.875rem;
          }

          /* Responsive improvements */
          @media (max-width: 576px) {
            .cap-modal .modal-footer {
              gap: 0.5rem;
              padding: 0.75rem;
            }
            
            .cap-modal .modal-footer .btn {
              padding: 0.375rem 0.75rem;
              width: 100%;
            }
            
            .cap-modal .modal-footer .order-1 {
              margin-bottom: 0.5rem;
            }
          }

          /* Make buttons more compact on small screens while keeping them usable */
          @media (max-width: 400px) {
            .cap-modal .modal-footer .btn {
              font-size: 0.8125rem;
              padding: 0.35rem 0.65rem;
            }
          }
        `}
      </style>
    </Modal>
  );
};

// Modified QuestionCard with automatic user selection and optional fields
const QuestionCard = ({ question, capActions, errors, handleInputChange, departmentUsers }) => {
  // Auto-select the department user when component loads or when departmentUsers changes
  useEffect(() => {
    // Skip if this question already has a responsible person assigned
    if (capActions[question.question_id]?.responsiblePerson) {
      return;
    }
    
    // If we have department users, select the first one automatically
    if (departmentUsers && departmentUsers.length > 0) {
      const firstUser = departmentUsers[0];
      const fullName = `${firstUser.firstName} ${firstUser.lastName}`;
      
      handleInputChange(
        question.question_id,
        'responsiblePerson',
        fullName
      );
    }
  }, [departmentUsers, question.question_id, handleInputChange, capActions]);

  return (
    <Card className="question-card mb-3 border">
      <Card.Body className="p-3">
        <div className="row gy-3">
          <div className="col-12 col-lg-6 mb-3 mb-lg-0">
            <div className="d-flex flex-column h-100">
              <strong className="mb-2 text-break question-text">
                {question.questionText}
              </strong>
              <div className="d-flex flex-wrap gap-2 align-items-center mt-auto">
                <span className="text-muted me-1">Answer:</span>
                <span className="text-break flex-grow-1">{question.answer}</span>
                <Badge className="flex-shrink-0" bg={question.points >= 8 ? 'success' : 'danger'}>
                  {question.points} points
                </Badge>
              </div>
            </div>
          </div>  
          <div className="col-12 col-lg-6">
            <div className="row g-3">
              <div className="col-12">
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Responsible Person 
                    <span className="text-muted ms-1">(auto-assigned)</span>
                  </Form.Label>
                  
                  {/* Display as text instead of editable input */}
                  <div className="form-control form-control-sm bg-light text-secondary">
                    {capActions[question.question_id]?.responsiblePerson || 
                      (departmentUsers && departmentUsers.length > 0 ? 
                        `${departmentUsers[0].firstName} ${departmentUsers[0].lastName}` : 
                        'Not assigned')}
                  </div>
                  
                  {departmentUsers && departmentUsers.length > 0 && (
                    <small className="text-muted d-block mt-1">
                      Department: {question.subsection} 
                      ({departmentUsers.length} user{departmentUsers.length !== 1 ? 's' : ''})
                    </small>
                  )}
                  
                  {(!departmentUsers || departmentUsers.length === 0) && (
                    <small className="text-danger d-block mt-1">
                      Warning: No department users found for {question.subsection || 'this section'}
                    </small>
                  )}
                </Form.Group>
              </div>
              <div className="col-12">
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Due Date 
                    <span className="text-danger ms-1">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    min={new Date().toISOString().split('T')[0]}
                    value={capActions[question.question_id]?.dueDate || ''}
                    onChange={(e) => handleInputChange(
                      question.question_id,
                      'dueDate',
                      e.target.value
                    )}
                    isInvalid={errors[question.question_id]?.dueDate}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[question.question_id]?.dueDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default JBranchCAPModel;